import { useMemo } from 'react';
import { colors } from '@/lib/colors';

/**
 * useMembershipTheme
 * ------------------
 * Phase 2 UX 통일화 훅. PT/그룹 수강권의 시각 표현(배경 gradient, 배지 tone, 정책 라벨)
 * 을 단일 소스에서 계산해 컴포넌트 분기를 제거한다.
 *
 * 이전에는 `isGroup`, `membership?.BGColor`, `expiryPolicy` 등의 조건이 여러 파일에
 * 흩어져 중복돼 있었음.
 *
 * @see BACKEND_RESPONSE_MEMBERSHIP_UNIFIED_V1.md §1 REQ-1/REQ-2
 */

export type MembershipSource = 'pt' | 'group';
export type MembershipExpiryPolicy = 'COUNT' | 'PERIOD' | 'FIRST';

/**
 * 훅이 요구하는 최소 필드 shape. 구조적 타이핑을 사용해 `MemberMembershipDataType`,
 * `Membership`(center.ts), `MemberShipApiType` 등 다양한 수강권 타입을 모두 받는다.
 */
export interface MembershipThemeInput {
    status?: string;
    source?: 'pt' | 'group';
    BGColor?: string[];
    expiryPolicy?: MembershipExpiryPolicy;
}

/** 그룹 수강권 기본 gradient (BGColor 미지원). 녹색 계열로 PT와 시각적으로 구별. */
export const GROUP_GRADIENT = 'linear-gradient(135deg, #10B981 0%, #065F46 100%)';

/** BGColor 없는 PT 수강권의 중립 fallback (회색). */
const NEUTRAL_GRADIENT = 'linear-gradient(135deg, #6B7280 0%, #374151 100%)';

export interface MembershipThemeResult {
    /** 소스 구분 */
    source: MembershipSource;
    isGroup: boolean;
    /** 현재 활성 상태 여부 (status === 'active') */
    isActive: boolean;
    /** 카드 헤더 gradient CSS value. status/소스/BGColor에 따라 자동 선택. */
    headerBackground: string;
    /** 만료 정책 (응답에 없으면 undefined — 배지 미표시) */
    expiryPolicy?: MembershipExpiryPolicy;
    /** 정책 표시 라벨 (한글) */
    expiryPolicyLabel?: string;
    /** 소스 라벨 ('그룹' | 'PT') — 카드 헤더 배지에 사용 */
    sourceLabel: string;
}

/**
 * 정책 enum → 한글 라벨.
 * UI에 그대로 노출되는 텍스트. 수정 시 UX 일관성 유의.
 */
export function getExpiryPolicyLabel(policy?: MembershipExpiryPolicy): string | undefined {
    if (!policy) return undefined;
    switch (policy) {
        case 'COUNT':
            return '회차 소진';
        case 'PERIOD':
            return '기간 만료';
        case 'FIRST':
            return '둘 중 먼저';
        default:
            return undefined;
    }
}

export function useMembershipTheme(
    membership: MembershipThemeInput | undefined | null,
): MembershipThemeResult {
    return useMemo(() => {
        const source: MembershipSource = membership?.source === 'group' ? 'group' : 'pt';
        const isGroup = source === 'group';
        // 그룹은 status가 'ACTIVE' (대문자), PT는 'active' (소문자) — 소문자 정규화로 통일
        // 일부 응답(정산 화면 등)은 그룹 membership에 status 자체가 누락됨 → 그룹이고 status 없으면 active로 간주
        const statusLower = (membership?.status ?? '').toString().toLowerCase();
        const isActive = statusLower === 'active' || (isGroup && !statusLower);

        // 카드 헤더 gradient 선택 우선순위:
        //  1) 비활성(expired) → 중립 회색
        //  2) 그룹 → GROUP_GRADIENT
        //  3) BGColor 2개 이상 → 그대로 사용
        //  4) fallback → 중립 gradient
        let headerBackground: string;
        if (!isActive) {
            headerBackground = colors.GREY_200;
        } else if (isGroup) {
            headerBackground = GROUP_GRADIENT;
        } else if (membership?.BGColor && membership.BGColor.length >= 2) {
            headerBackground = `linear-gradient(135deg, ${membership.BGColor[0]} 0%, ${membership.BGColor[1]} 100%)`;
        } else {
            headerBackground = NEUTRAL_GRADIENT;
        }

        // 레거시 PT 데이터는 expiryPolicy 미설정 → 백엔드 정책상 'COUNT' 취급
        // 그룹은 백엔드가 항상 채워서 응답하므로 fallback 불필요
        const expiryPolicy =
            membership?.expiryPolicy ?? (isGroup ? undefined : ('COUNT' as MembershipExpiryPolicy));
        const expiryPolicyLabel = getExpiryPolicyLabel(expiryPolicy);

        return {
            source,
            isGroup,
            isActive,
            headerBackground,
            expiryPolicy,
            expiryPolicyLabel,
            sourceLabel: isGroup ? '그룹' : 'PT',
        };
    }, [membership]);
}
